import React, { useState, useRef, useEffect } from 'react';
import './TimePicker.css';

const TimePicker = ({ 
  value, 
  onChange,
  disabled = false,
  showMeridian = false,
  placeholder = "Select Time"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedHour, setSelectedHour] = useState(() => {
    if (!value) return 0;
    if (typeof value === 'string') {
      const [hour] = value.split(':').map(Number);
      return hour || 0;
    }
    return value.getHours();
  });
  const [selectedMinute, setSelectedMinute] = useState(() => {
    if (!value) return 0;
    if (typeof value === 'string') {
      const [, minute] = value.split(':').map(Number);
      return minute || 0;
    }
    return value.getMinutes();
  });
  const pickerRef = useRef(null);

  const hours = showMeridian 
    ? Array.from({ length: 12 }, (_, i) => i === 0 ? 12 : i)
    : Array.from({ length: 24 }, (_, i) => i);
  const minutes = Array.from({ length: 60 }, (_, i) => i);

  useEffect(() => {
    if (value) {
      if (typeof value === 'string') {
        const [hour, minute] = value.split(':').map(Number);
        setSelectedHour(hour || 0);
        setSelectedMinute(minute || 0);
      } else {
        setSelectedHour(value.getHours());
        setSelectedMinute(value.getMinutes());
      }
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const formatTime = () => {
    if (!value) return '';
    
    let hours, minutes;
    if (typeof value === 'string') {
      [hours, minutes] = value.split(':').map(Number);
    } else {
      hours = value.getHours();
      minutes = value.getMinutes();
    }
    
    if (showMeridian) {
      const period = hours >= 12 ? 'PM' : 'AM';
      const hour12 = hours % 12 || 12;
      return `${String(hour12).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${period}`;
    }
    
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  };

  const handleTimeChange = (hour, minute) => {
    const formattedTime = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    console.log("New time selected:", formattedTime);
    
    if (onChange) {
      onChange(formattedTime);
    }
  };

  const handleHourClick = (hour) => {
    setSelectedHour(hour);
    handleTimeChange(hour, selectedMinute);
  };

  const handleMinuteClick = (minute) => {
    setSelectedMinute(minute);
    handleTimeChange(selectedHour, minute);
  };

  const handleOk = () => {
    setIsOpen(false);
  };

  const handleOpen = () => {
    if (disabled) return;
    setIsOpen(true);
  };

  return (
    <div className="custom-timepicker" ref={pickerRef}>
      <div 
        className="timepicker-input"
        style={{ pointerEvents: disabled ? 'none' : 'auto', backgroundColor: disabled ? '#e9ecef' : 'transparent' }}
        onClick={handleOpen}
      >
        <input
          type="text"
          value={formatTime()}
          placeholder={placeholder}
          readOnly
        />
      </div>
      
      {isOpen && (
        <div className="timepicker-dropdown">
          <div className="timepicker-content">
            <div className="timepicker-column">
              <div className="timepicker-column-title">Hours</div>
              <div className="timepicker-column-list">
                {hours.map((hour) => (
                  <div
                    key={hour}
                    className={`timepicker-cell ${selectedHour === hour ? 'selected' : ''}`}
                    onClick={() => handleHourClick(hour)}
                  >
                    {String(hour).padStart(2, '0')}
                  </div>
                ))}
              </div>
            </div>
            
            <div className="timepicker-column">
              <div className="timepicker-column-title">Minutes</div>
              <div className="timepicker-column-list">
                {minutes.map((minute) => (
                  <div
                    key={minute}
                    className={`timepicker-cell ${selectedMinute === minute ? 'selected' : ''}`}
                    onClick={() => handleMinuteClick(minute)}
                  >
                    {String(minute).padStart(2, '0')}
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <div className="timepicker-footer">
            <button 
              className="timepicker-ok-btn"
              onClick={handleOk}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimePicker;
